<script setup>
import { computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  pauseType: {
    type: Object,
    default: () => ({}),
  },
  submitLabel: {
    type: String,
    required: true,
  },
  isSubmitting: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const form = reactive({
  name: props.pauseType?.name || '',
  duration_minutes: props.pauseType?.duration_minutes || '',
  active: props.pauseType?.active !== undefined ? props.pauseType.active : true,
});

watch(
  () => props.pauseType,
  newPauseType => {
    form.name = newPauseType?.name || '';
    form.duration_minutes = newPauseType?.duration_minutes || '';
    form.active =
      newPauseType?.active !== undefined ? newPauseType.active : true;
  }
);

const nameError = computed(() => {
  if (!form.name.trim()) {
    return t('AGENT_PAUSE_TYPES.FORM.NAME.ERROR');
  }
  return '';
});

const durationError = computed(() => {
  if (!form.duration_minutes || Number(form.duration_minutes) < 1) {
    return t('AGENT_PAUSE_TYPES.FORM.DURATION.ERROR');
  }
  return '';
});

const isValid = computed(() => !nameError.value && !durationError.value);

const onSubmit = () => {
  if (!isValid.value) return;
  emit('submit', {
    name: form.name.trim(),
    duration_minutes: Number(form.duration_minutes),
    active: form.active,
  });
};
</script>

<template>
  <form class="flex flex-col gap-6" @submit.prevent="onSubmit">
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12" for="pause-type-name">
        {{ t('AGENT_PAUSE_TYPES.FORM.NAME.LABEL') }}
      </label>
      <input
        id="pause-type-name"
        v-model="form.name"
        type="text"
        class="w-full rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
        :placeholder="t('AGENT_PAUSE_TYPES.FORM.NAME.PLACEHOLDER')"
      />
      <span v-if="nameError" class="text-xs text-n-ruby-9">
        {{ nameError }}
      </span>
    </div>

    <div class="flex flex-col gap-2">
      <label
        class="text-sm font-medium text-n-slate-12"
        for="pause-type-duration"
      >
        {{ t('AGENT_PAUSE_TYPES.FORM.DURATION.LABEL') }}
      </label>
      <input
        id="pause-type-duration"
        v-model="form.duration_minutes"
        type="number"
        min="1"
        class="w-full rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
        :placeholder="t('AGENT_PAUSE_TYPES.FORM.DURATION.PLACEHOLDER')"
      />
      <span v-if="durationError" class="text-xs text-n-ruby-9">
        {{ durationError }}
      </span>
    </div>

    <div class="flex items-center gap-3">
      <input
        id="pause-type-active"
        v-model="form.active"
        type="checkbox"
        class="h-4 w-4 rounded border-n-weak"
      />
      <label class="text-sm font-medium text-n-slate-12" for="pause-type-active">
        {{ t('AGENT_PAUSE_TYPES.FORM.ACTIVE.LABEL') }}
      </label>
    </div>

    <div class="flex items-center justify-end gap-2">
      <Button
        faded
        slate
        type="button"
        :label="t('AGENT_PAUSE_TYPES.FORM.CANCEL')"
        @click="emit('cancel')"
      />
      <Button
        type="submit"
        :label="submitLabel"
        :disabled="!isValid || isSubmitting"
        :is-loading="isSubmitting"
      />
    </div>
  </form>
</template>
